import pdfMake from 'pdfmake/build/pdfmake'
import { MagnifyingGlass } from 'phosphor-react'
import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Card,
  Col,
  Container,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap'
import { BsDownload } from 'react-icons/bs'
import Navbar from '../../components/navbar/navbar'
import { CustomButton } from '../../components/CustomButton'
import { useOrdenation } from '../../hooks/useOrdenation/index.js'
import apinode from '../../services/apinode.js'

const POLLING_INTERVAL = 2000

const SemMovimentacao = () => {
  const [produtos, setProdutos]   = useState([])
  const [familias, setFamilias]   = useState([])
  const [loading, setLoading]     = useState(false)
  const [erro, setErro]           = useState(null)
  const [busca, setBusca]         = useState('')
  const [dias, setDias]           = useState(90)
  const [familia, setFamilia]     = useState('')
  const [tipoBusca, setTipoBusca] = useState('sku')
  const [statusMsg, setStatusMsg] = useState('')
  const [dots, setDots]           = useState('')

  const pollingRef = useRef(null)

  // ── Animação de pontinhos ───────────────────────────────────
  useEffect(() => {
    if (!loading) { setDots(''); return }
    const t = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500)
    return () => clearInterval(t)
  }, [loading])

  // ── Limpa polling ao desmontar ──────────────────────────────
  useEffect(() => () => clearInterval(pollingRef.current), [])

  // ── Busca famílias ──────────────────────────────────────────
  useEffect(() => {
    apinode.get(`/estoque/marca/${localStorage.idconta}`)
      .then(({ data }) => {
        const fmt = data.map(f => ({ id: f.ID_MARCA, nome: f.DESC_MARCA }))
        setFamilias([{ id: '', nome: 'Todas' }, ...fmt])
      })
      .catch(() => setErro('Não foi possível carregar as famílias.'))
  }, [])

  // ── Para o polling de forma segura ─────────────────────────
  const pararPolling = () => {
    clearInterval(pollingRef.current)
    pollingRef.current = null
  }

  // ── Finaliza com sucesso ────────────────────────────────────
  const finalizarComSucesso = (resultado) => {
    // garante array independente do que vier
    const lista = Array.isArray(resultado) ? resultado : []
    setProdutos(lista)
    setStatusMsg('')
    setLoading(false)
  }

  // ── Finaliza com erro ───────────────────────────────────────
  const finalizarComErro = (mensagem) => {
    setErro(mensagem)
    setStatusMsg('')
    setLoading(false)
  }

  // ── Polling ─────────────────────────────────────────────────
  const iniciarPolling = (jobId) => {
    pararPolling() // garante que não tem dois intervalos rodando

    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await apinode.get(`/sem-movimentacao-status/${jobId}`)

        if (data.status === 'processing') {
          setStatusMsg('Processando relatório')
          return
        }

        pararPolling()

        if (data.status === 'expired') {
          finalizarComErro('Relatório expirou — tente gerar novamente.')
          return
        }

        if (data.status === 'error') {
          const detalhe = data.detalhe?.sqlMessage
            ? `\nDetalhe: ${data.detalhe.sqlMessage}`
            : ''
          finalizarComErro((data.erro ?? 'Erro ao processar relatório.') + detalhe)
          return
        }

        if (data.status === 'done') {
          finalizarComSucesso(data.resultado)
          return
        }

        // status desconhecido — não trava o usuário
        finalizarComErro('Resposta inesperada do servidor.')

      } catch (e) {
        pararPolling()
        finalizarComErro('Erro ao verificar status do relatório.')
      }
    }, POLLING_INTERVAL)
  }

  // ── Dispara a busca ─────────────────────────────────────────
  const buscarProdutos = async () => {
    pararPolling()
    setLoading(true)
    setErro(null)
    setProdutos([])
    setStatusMsg('Iniciando consulta')

    try {
      const params = tipoBusca === 'sku' ? { sku: busca } : { descricao: busca }
      if (familia) params.marca = familia
      if (dias)    params.dias  = dias

      const { data } = await apinode.get(
        `/sem-movimentacao/${localStorage.idconta}`,
        { params },
      )

      if (data.jobId) {
        // job em processamento ou já finalizado no cache
        if (data.status === 'done') {
          // backend reutilizou job já pronto — busca o resultado direto
          const { data: statusData } = await apinode.get(`/sem-movimentacao-status/${data.jobId}`)
          if (statusData.status === 'done') {
            finalizarComSucesso(statusData.resultado)
          } else {
            // improvável mas seguro
            setStatusMsg('Processando relatório')
            iniciarPolling(data.jobId)
          }
          return
        }

        setStatusMsg('Processando relatório')
        iniciarPolling(data.jobId)
        return
      }

      // fallback: servidor retornou array direto
      finalizarComSucesso(data)

    } catch {
      finalizarComErro('Erro ao iniciar a busca.')
    }
  }

  // ── sempre passa array garantido para o hook ────────────────
  const listaProdutos = Array.isArray(produtos) ? produtos : []

  const { filteredItems, Th, Paginate, itemsPerPage, setItemsPerPage } =
    useOrdenation(listaProdutos, 'dias_sem_venda', 'desc', '50')

  // ── PDF ─────────────────────────────────────────────────────
  const gerarPDF = () => {
    const cols = ['SKU Pai', 'Descrição', 'Marca', 'Estoque', 'Última Venda', 'Dias s/ Venda']
    const agora = new Date()

    const rows = listaProdutos.map(p => [
      { text: p.sku_pai,                                                                    fontSize: 7 },
      { text: p.descricao?.length > 80 ? p.descricao.slice(0, 77) + '...' : p.descricao,  fontSize: 7 },
      { text: p.marca ?? '—',                                                               fontSize: 7 },
      { text: p.estoque_total,                                                              fontSize: 7 },
      { text: p.ultima_venda ? new Date(p.ultima_venda).toLocaleDateString('pt-BR') : '—', fontSize: 7 },
      { text: p.dias_sem_venda ?? '—',                                                      fontSize: 7 },
    ])

    pdfMake.createPdf({
      pageOrientation: 'landscape',
      pageSize: 'A4',
      pageMargins: [30, 10, 30, 10],
      content: [
        { text: 'Relatório — Produtos Sem Movimentação', style: 'header' },
        { text: `Data: ${agora.toLocaleDateString()} ${agora.toLocaleTimeString()}`, style: 'sub' },
        { text: `Período analisado: ${dias} dias`, style: 'sub' },
        {
          style: 'tableExample',
          table: {
            headerRows: 1,
            widths: [40, '*', 80, 35, 60, 55],
            body: [
              cols.map(c => ({ text: c, fontSize: 8, bold: true })),
              ...rows,
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ],
      styles: {
        header:       { fontSize: 16, bold: true,   alignment: 'center', margin: [0, 0, 0, 6] },
        sub:          { fontSize: 9,  italics: true, alignment: 'center', margin: [0, 2, 0, 4] },
        tableExample: { margin: [0, 5, 0, 15] },
      },
    }).open()
  }

  // ── helpers ─────────────────────────────────────────────────
  const corDias = (d) => {
    if (d === null || d === undefined) return '#9ca3af'
    if (d >= 180) return '#ef4444'
    if (d >= 90)  return '#f59e0b'
    return '#10b981'
  }

  const formatDate = (s) =>
    s ? new Date(s).toLocaleDateString('pt-BR') : '—'

  // ── render ──────────────────────────────────────────────────
  return (
    <>
      <Navbar />
      <Container className="mt-page">
        <Card>
          <Card.Header as="h5">Produtos Sem Movimentação</Card.Header>
          <Card.Body>

            {/* ── Filtros ── */}
            <Form className="mb-4">
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>SKU ou Descrição</Form.Label>
                    <Form.Control
                      type="text"
                      value={busca}
                      onChange={e => setBusca(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && buscarProdutos()}
                      placeholder="Digite o SKU ou nome"
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Período (dias)</Form.Label>
                    <Form.Control
                      type="number"
                      value={dias}
                      onChange={e => setDias(Number(e.target.value))}
                      placeholder="Ex: 90"
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Família de produtos</Form.Label>
                    <Form.Select value={familia} onChange={e => setFamilia(e.target.value)}>
                      {familias.map(f => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Produtos p/ página</Form.Label>
                    <Form.Select value={itemsPerPage} onChange={e => setItemsPerPage(e.target.value)}>
                      {['10', '20', '50', '100'].map(v => <option key={v} value={v}>{v}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={10}>
                  <Form.Label column sm={3}>Tipo de busca</Form.Label>
                  <Form.Check inline label="SKU"       type="radio" id="sku"
                    name="tipoBusca" value="sku"
                    checked={tipoBusca === 'sku'}
                    onChange={() => setTipoBusca('sku')} />
                  <Form.Check inline label="Descrição" type="radio" id="descricao"
                    name="tipoBusca" value="descricao"
                    checked={tipoBusca === 'descricao'}
                    onChange={() => setTipoBusca('descricao')} />
                </Col>
              </Row>

              <Row>
                <Col>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <CustomButton noMargin title="Buscar" onClick={buscarProdutos} isLoading={loading}>
                      <MagnifyingGlass />
                    </CustomButton>
                    <CustomButton noMargin isSuccess title="Baixar PDF" onClick={gerarPDF} isDisabled={listaProdutos.length === 0}>
                      <BsDownload />
                    </CustomButton>
                  </div>
                </Col>
              </Row>
            </Form>

            {/* ── Estados ── */}
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-2 fw-semibold">{statusMsg}{dots}</p>
                <ProgressBar animated now={100} style={{ maxWidth: 320, margin: '0 auto' }} />
                <p className="text-muted mt-2" style={{ fontSize: '.8rem' }}>
                  Isso pode levar alguns segundos dependendo do volume de dados.
                </p>
              </div>
            ) : erro ? (
              <Alert variant="danger" dismissible onClose={() => setErro(null)}>
                {erro}
              </Alert>
            ) : listaProdutos.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: '.78rem', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontWeight: 600 }}>Dias sem venda:</span>
                  {[
                    { cor: '#10b981', label: '< 90 dias'   },
                    { cor: '#f59e0b', label: '90–179 dias' },
                    { cor: '#ef4444', label: '≥ 180 dias'  },
                  ].map(({ cor, label }) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: cor, display: 'inline-block' }} />
                      {label}
                    </span>
                  ))}
                  <span style={{ marginLeft: 'auto', color: '#6b7280' }}>
                    Mostrando {filteredItems.length} de {listaProdutos.length}
                  </span>
                </div>

                <Table striped bordered hover responsive className="small">
                  <thead>
                    <tr>
                      <Th label="SKU Pai"      value="sku_pai"        />
                      <Th label="Descrição"     value="descricao"      />
                      <Th label="Marca"         value="marca"          />
                      <Th label="Estoque"       value="estoque_total"  />
                      <Th label="Última Venda"  value="ultima_venda"   />
                      <Th label="Dias s/ Venda" value="dias_sem_venda" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((p, i) => (
                      <tr key={i}>
                        <td>{p.sku_pai}</td>
                        <td>{p.descricao}</td>
                        <td>{p.marca ?? '—'}</td>
                        <td>{p.estoque_total}</td>
                        <td>{formatDate(p.ultima_venda)}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: corDias(p.dias_sem_venda) }}>
                            {p.dias_sem_venda ?? '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Paginate />
              </>
            ) : null}

          </Card.Body>
        </Card>
      </Container>
    </>
  )
}

export default SemMovimentacao